import { loopsToSegments } from "@/components/r3f/renderers/analyses/volume-outline-geometry";
import { contours } from "d3-contour";
import simplify from "simplify-js";
import {
  cylinderDistance,
  cylinderNormal,
  type CylinderSurface,
} from "./cylinder-surface";
import {
  Box2,
  Matrix4,
  Plane,
  ShapeUtils,
  Triangle,
  Vector2,
  Vector3,
} from "three";

const PIXELS_PER_SLICE = 8192;
const MIN_RAY_PLANE_COSINE = 0.02;
const MIN_NORMAL_ALIGNMENT = Math.cos(Math.PI / 12);
const MAX_VISUAL_HOLE_AREA = 9;
const OUTLINE_SIMPLIFICATION = 3;
const OUTLINE_MEDIAN_RADIUS = 4;
const MIN_PROJECTED_UP_LENGTH_SQUARED = 0.000001;

export type DepthSnapshot = {
  depth: Float32Array;
  width: number;
  height: number;
  inverseViewProjection: Matrix4;
  cameraInverseViewProjection?: Matrix4;
};

export type DepthSurface = {
  mask: Uint8Array;
  count: number;
};

/**
 * @param positions Local, non-indexed surface triangles in meters.
 * @param normal World-space plane normal.
 * @param up Viewer world-up direction.
 * @param worldGrid Optional world mask, authoritative for area and cylinder dimensions.
 * @returns Display-plane extents, triangle area and a local callout anchor.
 */
export function surfaceDimensions(
  positions: Float32Array,
  normal: Vector3,
  up: Vector3,
  worldGrid?: Pick<
    DepthSnapshot,
    "width" | "height" | "inverseViewProjection"
  > & { mask: Uint8Array; cylinder?: CylinderSurface },
):
  | { width: number; height: number; area: number; anchor: Vector3 }
  | undefined {
  if (!positions.length || positions.length % 9 !== 0) return;
  if (worldGrid?.cylinder) {
    const { axis, radius } = worldGrid.cylinder;
    const point = new Vector3();
    let minimum = Infinity;
    let maximum = -Infinity;
    for (let offset = 0; offset < positions.length; offset += 3) {
      const axial = point.fromArray(positions, offset).dot(axis);
      minimum = Math.min(minimum, axial);
      maximum = Math.max(maximum, axial);
    }
    const height = maximum - minimum;
    const anchor = new Vector3().fromArray(positions);
    anchor.addScaledVector(axis, (minimum + maximum) / 2 - anchor.dot(axis));
    const cells = worldGrid.mask.reduce(
      (sum, value) => sum + (value ? 1 : 0),
      0,
    );
    return {
      width: radius * 2,
      height,
      area:
        (((cells * Math.PI * 2 * radius) / worldGrid.width) * height) /
        worldGrid.height,
      anchor,
    };
  }
  const vertical = up.clone().projectOnPlane(normal);
  if (vertical.lengthSq() < MIN_PROJECTED_UP_LENGTH_SQUARED) {
    vertical.set(0, 1, 0).projectOnPlane(normal);
    if (vertical.lengthSq() < MIN_PROJECTED_UP_LENGTH_SQUARED) {
      vertical.set(1, 0, 0).projectOnPlane(normal);
    }
  }
  vertical.normalize();
  const horizontal = new Vector3().crossVectors(vertical, normal).normalize();
  const bounds = new Box2();
  const projected = new Vector2();
  const triangle = new Triangle();
  const anchor = new Vector3();
  const midpoint = new Vector3();
  let area = 0;
  for (let offset = 0; offset < positions.length; offset += 9) {
    triangle.a.fromArray(positions, offset);
    triangle.b.fromArray(positions, offset + 3);
    triangle.c.fromArray(positions, offset + 6);
    for (const point of [triangle.a, triangle.b, triangle.c]) {
      bounds.expandByPoint(
        projected.set(point.dot(horizontal), point.dot(vertical)),
      );
    }
    const triangleArea = triangle.getArea();
    area += triangleArea;
    anchor.addScaledVector(triangle.getMidpoint(midpoint), triangleArea);
  }
  if (!Number.isFinite(area) || area <= 0) return;
  anchor.divideScalar(area);
  if (worldGrid) {
    const horizontalExtent = new Vector3().setFromMatrixColumn(
      worldGrid.inverseViewProjection,
      0,
    );
    const verticalExtent = new Vector3().setFromMatrixColumn(
      worldGrid.inverseViewProjection,
      1,
    );
    const cellArea =
      (horizontalExtent.cross(verticalExtent).length() * 4) /
      (worldGrid.width * worldGrid.height);
    area =
      worldGrid.mask.reduce((count, value) => count + (value ? 1 : 0), 0) *
      cellArea;
  }
  const size = bounds.getSize(projected);
  return { width: size.x, height: size.y, area, anchor };
}

/**
 * @param snapshot Captured depth and camera transform.
 * @param column Bottom-origin pixel column.
 * @param row Bottom-origin pixel row.
 * @param target Reusable output vector.
 * @returns World position at a depth pixel center.
 */
export function depthPosition(
  snapshot: DepthSnapshot,
  column: number,
  row: number,
  target = new Vector3(),
): Vector3 {
  return target
    .set(
      ((column + 0.5) / snapshot.width) * 2 - 1,
      ((row + 0.5) / snapshot.height) * 2 - 1,
      snapshot.depth[row * snapshot.width + column] * 2 - 1,
    )
    .applyMatrix4(snapshot.inverseViewProjection);
}

/**
 * @param snapshot Captured depth grid.
 * @param seed Seed pixel index.
 * @param plane Fixed world-space plane or cylinder.
 * @param tolerance Maximum perpendicular distance in meters.
 * @param cancelled Whether a newer request superseded this one.
 * @returns The seed-connected part of the fixed surface.
 */
export async function selectDepthSurface(
  snapshot: DepthSnapshot,
  seed: number,
  plane: Plane | CylinderSurface,
  tolerance: number,
  cancelled: () => boolean = () => false,
): Promise<DepthSurface | undefined> {
  const { width, height, depth } = snapshot;
  if (seed < 0 || seed >= depth.length || width * height !== depth.length) {
    return;
  }
  const mask = new Uint8Array(depth.length);
  const visited = new Uint8Array(depth.length);
  const queue = new Uint32Array(depth.length);
  const point = new Vector3();
  const horizontal = new Vector3();
  const vertical = new Vector3();
  const neighbor = new Vector3();
  const forward = new Vector3();
  const backward = new Vector3();
  const centered = new Vector3();
  const normal = new Vector3();
  const expectedNormal = new Vector3();
  let count = 0;
  let cursor = 0;
  function sampleDirection(
    column: number,
    row: number,
    target: Vector3,
  ): boolean {
    if (column < 0 || column >= width || row < 0 || row >= height) return false;
    const sample = depth[row * width + column];
    if (!Number.isFinite(sample) || sample <= 0 || sample >= 1) return false;
    depthPosition(snapshot, column, row, neighbor);
    target.subVectors(neighbor, point);
    return target.lengthSq() > 0;
  }
  function direction(
    column: number,
    row: number,
    columnStep: number,
    rowStep: number,
    target: Vector3,
  ): boolean {
    const hasForward = sampleDirection(
      column + columnStep,
      row + rowStep,
      forward,
    );
    const hasBackward = sampleDirection(
      column - columnStep,
      row - rowStep,
      backward,
    );
    if (!hasForward && !hasBackward) return false;
    backward.negate();
    target.copy(hasForward ? forward : backward);
    if (hasForward && hasBackward) {
      centered.addVectors(forward, backward);
      for (const candidate of [backward, centered]) {
        if (
          candidate.lengthSq() > 0 &&
          Math.abs(candidate.dot(expectedNormal)) / candidate.length() <
            Math.abs(target.dot(expectedNormal)) / target.length()
        ) {
          target.copy(candidate);
        }
      }
    }
    return true;
  }
  function visit(index: number): void {
    if (visited[index]) return;
    visited[index] = 1;
    if (
      !Number.isFinite(depth[index]) ||
      depth[index] >= 1 ||
      depth[index] <= 0
    ) {
      return;
    }
    depthPosition(snapshot, index % width, Math.floor(index / width), point);
    if ("radius" in plane) {
      if (Math.abs(cylinderDistance(plane, point)) > tolerance) return;
      cylinderNormal(plane, point, expectedNormal);
    } else {
      if (Math.abs(plane.distanceToPoint(point)) > tolerance) return;
      expectedNormal.copy(plane.normal);
    }
    const column = index % width;
    const row = Math.floor(index / width);
    const step = "radius" in plane ? 1 : 2;
    if (
      direction(column, row, step, 0, horizontal) &&
      direction(column, row, 0, step, vertical)
    ) {
      normal.crossVectors(horizontal, vertical);
      if (
        normal.lengthSq() > 0 &&
        Math.abs(normal.normalize().dot(expectedNormal)) < MIN_NORMAL_ALIGNMENT
      ) {
        return;
      }
    }
    mask[index] = 255;
    queue[count++] = index;
  }
  visit(seed);
  while (cursor < count) {
    if (cancelled()) return;
    const end = cursor + PIXELS_PER_SLICE;
    while (cursor < count && cursor < end) {
      const index = queue[cursor++];
      const column = index % width;
      if (column > 0) visit(index - 1);
      if (column + 1 < width) visit(index + 1);
      if (index >= width) visit(index - width);
      if (index + width < width * height) visit(index + width);
    }
    if (cursor < count) {
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
    }
  }
  return count >= 12 ? { mask, count } : undefined;
}

/**
 * @param snapshot Captured camera and dimensions.
 * @param mask Selected pixels.
 * @param plane Fitted world-space plane.
 * @param origin Local geometry origin to preserve precision.
 * @returns Simplified planar triangles and boundaries, retaining larger holes.
 */
export function surfaceGeometry(
  snapshot: DepthSnapshot,
  mask: Uint8Array,
  plane: Plane,
  origin: Vector3,
): { positions: Float32Array; outline: Float32Array } {
  const { width, height, inverseViewProjection } = snapshot;
  const positions: number[] = [];
  const loops: Vector3[][] = [];
  const near = new Vector3();
  const far = new Vector3();
  function corner(column: number, row: number): Vector3 | undefined {
    near
      .set((column / width) * 2 - 1, (row / height) * 2 - 1, -1)
      .applyMatrix4(inverseViewProjection);
    far
      .set((column / width) * 2 - 1, (row / height) * 2 - 1, 1)
      .applyMatrix4(inverseViewProjection);
    const direction = far.sub(near).normalize();
    const denominator = plane.normal.dot(direction);
    if (Math.abs(denominator) < MIN_RAY_PLANE_COSINE) return;
    const distance = -plane.distanceToPoint(near) / denominator;
    if (distance < 0 || !Number.isFinite(distance)) return;
    return near.clone().addScaledVector(direction, distance).sub(origin);
  }
  const polygons = contours()
    .size([width, height])
    .smooth(false)
    .contour(Array.from(mask), 1);
  for (const polygon of polygons.coordinates) {
    const rings = polygon
      .map((ring) => ring.map(([column, row]) => new Vector2(column, row)))
      .filter(
        (ring, index) =>
          index === 0 || Math.abs(ShapeUtils.area(ring)) > MAX_VISUAL_HOLE_AREA,
      )
      .map((ring) => {
        const openRing = ring.slice(0, -1);
        const smoothed = openRing.map((point, index) => {
          if (openRing.length <= OUTLINE_MEDIAN_RADIUS * 2) return point;
          const neighbors = Array.from(
            { length: OUTLINE_MEDIAN_RADIUS * 2 + 1 },
            (_, offset) =>
              openRing[
                (index + offset - OUTLINE_MEDIAN_RADIUS + openRing.length) %
                  openRing.length
              ],
          );
          return new Vector2(
            neighbors
              .map((neighbor) => neighbor.x)
              .sort((left, right) => left - right)[OUTLINE_MEDIAN_RADIUS],
            neighbors
              .map((neighbor) => neighbor.y)
              .sort((left, right) => left - right)[OUTLINE_MEDIAN_RADIUS],
          );
        });
        smoothed.push(smoothed[0]);
        const simplified = simplify(
          smoothed,
          OUTLINE_SIMPLIFICATION,
          true,
        ).slice(0, -1);
        return simplified.length >= 3 ? simplified : openRing;
      });
    const projected: Vector3[][] = [];
    for (const ring of rings) {
      const vertices: Vector3[] = [];
      for (const point of ring) {
        const vertex = corner(point.x, point.y);
        if (vertex) vertices.push(vertex);
      }
      if (vertices.length !== ring.length) break;
      projected.push(vertices);
    }
    if (projected.length !== rings.length) continue;
    const vertices = projected.flat();
    for (const triangle of ShapeUtils.triangulateShape(
      rings[0],
      rings.slice(1),
    )) {
      for (const index of triangle) {
        positions.push(...vertices[index].toArray());
      }
    }
    loops.push(...projected);
  }
  return {
    positions: new Float32Array(positions),
    outline: loopsToSegments(loops),
  };
}
